// RegionalRolluCard — compact regional grouping of grading + coaching
// activity across the master-admin domain fleet. Answers "How is
// Brisbane doing vs Sydney this month?" at a glance without leaving
// the overview. Domains without an assigned region roll up as
// "Unassigned" so nothing is silently dropped.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Globe, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const gradeTone = (v) => {
  if (v == null) return 'text-white/40';
  if (v >= 75) return 'text-emerald-300';
  if (v >= 60) return 'text-amber-300';
  return 'text-rose-300';
};

const RegionalRollupCard = ({ token }) => {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/admin/regional-rollup?days=30`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => setData(r.data))
      .catch(() => setData({ error: true }));
  }, [token]);

  if (!data) {
    return (
      <div className="bg-white/5 border border-white/10 rounded-xl p-5" data-testid="regional-rollup-loading">
        <Loader2 className="w-4 h-4 animate-spin text-white/40" />
      </div>
    );
  }
  if (data.error) return null;
  if (!data.regions?.length) return null;

  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-5" data-testid="regional-rollup">
      <div className="flex items-baseline justify-between mb-3">
        <div className="flex items-center gap-2">
          <Globe className="w-4 h-4 text-teal-400" />
          <h3 className="text-white font-semibold">Regional rollup</h3>
          <span className="text-xs text-white/40">· last {data.days} days</span>
        </div>
      </div>
      <div className="space-y-2">
        {data.regions.map((r) => {
          const isOpen = expanded[r.region];
          return (
            <div key={r.region} className="border border-white/10 rounded-lg" data-testid={`region-row-${r.region}`}>
              <button
                type="button"
                onClick={() => setExpanded((e) => ({ ...e, [r.region]: !e[r.region] }))}
                className="w-full flex items-center gap-3 p-3 hover:bg-white/5 rounded-lg"
              >
                {isOpen ? <ChevronDown className="w-4 h-4 text-white/40" /> : <ChevronRight className="w-4 h-4 text-white/40" />}
                <div className="flex-1 text-left">
                  <p className="text-sm font-semibold text-white">
                    {r.region}
                    <span className="text-xs text-white/40 font-normal ml-2">· {r.domain_count} {r.domain_count === 1 ? 'tenant' : 'tenants'}</span>
                  </p>
                </div>
                <div className="grid grid-cols-3 gap-4 text-right shrink-0">
                  <div>
                    <p className="text-[10px] uppercase text-white/40">Calls</p>
                    <p className="text-white tabular-nums text-sm font-semibold">{r.total_calls.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase text-white/40">Avg grade</p>
                    <p className={`tabular-nums text-sm font-semibold ${gradeTone(r.avg_grade)}`}>
                      {r.avg_grade != null ? r.avg_grade : '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase text-white/40">Coaching</p>
                    <p className="text-white tabular-nums text-sm font-semibold">{r.active_coaching}</p>
                  </div>
                </div>
              </button>
              {isOpen && (
                <div className="border-t border-white/10 px-3 pt-2 pb-3 space-y-1">
                  {r.domains.map((d) => (
                    <div key={d.id} className="flex items-center gap-3 text-xs" data-testid={`region-domain-${d.id}`}>
                      <span className="text-white/70 flex-1 truncate">{d.company_name || d.yabbit_domain}</span>
                      <span className="text-white/50 tabular-nums w-16 text-right">{d.calls.toLocaleString()} calls</span>
                      <span className={`tabular-nums w-12 text-right ${gradeTone(d.avg_grade)}`}>
                        {d.avg_grade != null ? d.avg_grade : '—'}
                      </span>
                      <span className={`w-20 text-right text-[10px] uppercase font-semibold ${
                        d.training_program === 'tfvf'       ? 'text-purple-300' :
                        d.training_program === 'kb_generic' ? 'text-teal-300'   :
                                                              'text-white/30'
                      }`}>{d.training_program}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RegionalRollupCard;
